/**
 * Connectivity, one interface over two sources of truth.
 *
 * `navigator.onLine` and the window `online`/`offline` events are the obvious
 * implementation and the wrong one inside a Capacitor WebView. The app is
 * served from `https://localhost`, so the WebView's notion of "online" tracks
 * whether it can reach its own origin — which never changes. Coming out of
 * airplane mode or Doze, the events routinely don't fire at all.
 *
 * That is exactly why @capacitor/network exists, and why the Android manifest
 * already declares ACCESS_NETWORK_STATE. This module prefers the plugin and
 * falls back to the browser API, so the sync engine can stay platform-blind.
 *
 * Getting this wrong is survivable but confusing: sync would still recover on
 * the next foreground or the 5-minute tick, while the UI's offline indicator
 * lied about why. Rather than a silent lie, prefer the source that knows.
 */

import { loadPlugin } from './platform'

async function plugin() {
  return loadPlugin(() => import('@capacitor/network'), 'Network')
}

function webOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/** Current connectivity. Falls back to `navigator.onLine` off-device. */
export async function isOnline(): Promise<boolean> {
  const net = await plugin()
  if (!net) return webOnline()
  try {
    return (await net.getStatus()).connected
  } catch {
    return webOnline()
  }
}

/**
 * Subscribe to connectivity changes.
 *
 * Returns an unsubscribe function. The callback fires only on change, never
 * with an initial value — callers that need the current state ask `isOnline()`.
 */
export function watch(onChange: (online: boolean) => void): () => void {
  let removeNative: (() => void) | null = null
  let disposed = false

  void plugin().then((net) => {
    if (!net || disposed) return
    void net
      .addListener('networkStatusChange', (s: { connected: boolean }) => onChange(s.connected))
      .then((h: { remove: () => Promise<void> }) => {
        // Disposed while the listener was still being registered.
        if (disposed) void h.remove().catch(() => {})
        else removeNative = () => void h.remove().catch(() => {})
      })
      .catch((err: unknown) => {
        console.warn('[network] could not watch status, using web events:', err)
        addWebListeners()
      })
  })

  const onWebOnline = () => onChange(true)
  const onWebOffline = () => onChange(false)
  let webListening = false

  function addWebListeners() {
    if (webListening || disposed || typeof window === 'undefined') return
    webListening = true
    window.addEventListener('online', onWebOnline)
    window.addEventListener('offline', onWebOffline)
  }

  // On the web the plugin is never available, so wire these up immediately.
  // Natively they are the fallback the catch above installs.
  void plugin().then((net) => {
    if (!net) addWebListeners()
  })

  return () => {
    disposed = true
    removeNative?.()
    if (webListening && typeof window !== 'undefined') {
      window.removeEventListener('online', onWebOnline)
      window.removeEventListener('offline', onWebOffline)
    }
  }
}
